import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import Spinner from "components/base/Spinner";
import Errors from "components/base/Errors";
import { Button } from "components/base/Button";
import Page from "components/base/Page";
import { Alert } from "components/base/Toast";
import { parseError } from "apollo";
import useUploadProtection from "hooks/useUploadProtection";
import { FaSyncAlt } from "react-icons/fa";

const FETCH_LIFTSTYLE_IMAGES = gql`
  query FETCH_LIFTSTYLE_IMAGES(
    $first: Int!
    $after: String
    $name_Icontains: String
  ) {
    promoImages(first: $first, after: $after, name_Icontains: $name_Icontains) {
      edges {
        node {
          id
          name
          thumb: url(size: "300x300")
          url
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

const SYNC_LIFESTYLE_IMAGES = gql`
  mutation SYNC_LIFESTYLE_IMAGES {
    syncLifestyleImages {
      result
    }
  }
`;

function LiftstyleImages() {
  const [pageSize] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { loading, error, data, refetch, fetchMore } = useQuery(
    FETCH_LIFTSTYLE_IMAGES,
    {
      variables: {
        first: pageSize,
        after: null,
        name_Icontains: debouncedSearch || undefined,
      },
    },
  );

  const [sync, syncRes] = useMutation(SYNC_LIFESTYLE_IMAGES, {
    onError: (error) => {
      Alert("error", parseError(error));
    },
    onCompleted() {
      Alert("success", "Sync success");
      refetch();
    },
  });

  useUploadProtection(
    syncRes.loading,
    "Image sync is in progress. Leaving the page may cancel the sync. Continue?",
  );

  // 防抖：输入变化后延迟500ms更新搜索关键词
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleClearSearch = () => {
    setSearchInput("");
  };

  const handleLoadMore = () => {
    const endCursor = data?.promoImages?.pageInfo?.endCursor;
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;

          return {
            promoImages: {
              ...fetchMoreResult.promoImages,
              edges: [
                ...previousResult.promoImages.edges,
                ...fetchMoreResult.promoImages.edges,
              ],
            },
          };
        },
      });
    }
  };

  const handleLoadPrevious = () => {
    refetch({
      first: pageSize,
      after: null,
      name_Icontains: debouncedSearch || undefined,
    });
  };

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const edges = data?.promoImages?.edges || [];
  const images = edges.map((edge) => edge.node);
  const pageInfo = data?.promoImages?.pageInfo;
  const totalCount = data?.promoImages?.totalCount || 0;

  return (
    <Page
      title="Liftstyle Images"
      rightButtons={
        <Button
          onClick={() => {
            if (
              window.confirm(
                "Are you sure to sync lifestyle images from SharePoint to WIS?",
              )
            ) {
              sync();
            }
          }}
          loading={syncRes.loading}
          icon={<FaSyncAlt />}
        >
          Sync from SharePoint
        </Button>
      }
    >
      <div>
        <div className="m-6">
          <div className="opacity-70 space-y-2">
            <p>
              Please prepare the images in SharePoint (
              <a
                href="https://wabobaadmin.sharepoint.com/sites/Waboba/Shared%20Documents/Forms/AllItems.aspx?FolderCTID=0x012000ED1A44060EECF045B3DF12F1E8DEE39F&id=%2Fsites%2FWaboba%2FShared%20Documents%2Fwis%2Flifestyle&viewid=2ef7e44d%2D79b9%2D437a%2Dbc6f%2D674c8d62a938"
                rel="noreferer"
                target="_blank"
                className="mx-1"
              >
                Waboba / Documents / wis / lifestyle
              </a>
              ) first.
            </p>
            <p>Click the sync button on the top right to submit to WIS.</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 pb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search images..."
              className="border rounded-lg px-3 py-2 flex-1"
            />
            {searchInput && <Button onClick={handleClearSearch}>Clear</Button>}
          </div>
          {pageInfo && (
            <div className="mt-2 text-sm text-gray-600">
              Showing {images.length} of {totalCount} images
              {searchInput && ` (filtered by "${searchInput}")`}
            </div>
          )}
        </div>

        <div
          className="flex flex-wrap ml-6 content-start overflow-y-auto"
          style={{ maxHeight: "70vh" }}
        >
          {images.map((img, index) => (
            <div
              className="mr-6 mb-6 flex flex-col justify-between items-center flex-1"
              key={index}
            >
              <div className="w-48">
                <div className="relative flex justify-center">
                  <img className="h-32" src={img.thumb} alt="〄" />
                </div>
                <div className="pt-2 text-gray-500 text-xs break-all text-center">
                  {img.name}
                </div>
              </div>
              <div className="flex justify-center">
                <Button
                  link={img.thumb}
                  size="sm"
                  title="small"
                  target="_blank"
                ></Button>
                <Button
                  link={img.url}
                  className="ml-2"
                  size="sm"
                  title="large"
                  target="_blank"
                ></Button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pageInfo && (pageInfo.hasNextPage || pageInfo.hasPreviousPage) && (
          <div className="flex justify-center items-center gap-4 p-6">
            <Button
              prev
              onClick={handleLoadPrevious}
              disabled={
                !pageInfo.hasPreviousPage ||
                !data?.promoImages?.pageInfo?.startCursor
              }
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Showing {images.length} of {totalCount} images
            </span>
            <Button
              next
              onClick={handleLoadMore}
              disabled={!pageInfo.hasNextPage}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </Page>
  );
}

export default LiftstyleImages;
